import React from 'react';
import { useLocalizedText } from '../../../hooks/localizedText';
import { Button, Container, Spinner } from '@amzn/awsui-components-react';
import { Event, GetEventDetailsByEventIdCommandOutput } from '@amzn/red-velvet-api';

import { useAllPagesRedVelvetQuery } from '../../../hooks/allPagesRedVelvetQuery';
import { useUrlPage } from '../../../hooks/attendance/urlPage';
import { FIRST_PAGE } from '../wizard/attendanceWizard';
import { EventTitleHeader } from '../../events/eventTitleHeader';

import {
  DISPLAY_EVENT_ATTRIBUTES_TYPE,
  DISPLAY_EVENT_COORDINATOR_INFO,
  DISPLAY_EVENT_DATE_INFO,
  DISPLAY_EVENT_LOCATION_INFO,
  DISPLAY_EVENT_ORGANIZATION_INFO,
  DISPLAY_SHIFTS_COUNT_INFO,
  DISPLAY_TOTAL_SIGNUPS_INFO,
} from '../../events/eventInfo';
import { EventCard } from './eventCard';

interface EventAttendanceHeaderProps {
  event: Event;
  isEventFetched: boolean;
}

export function EventAttendanceHeader({ event, isEventFetched }: EventAttendanceHeaderProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const { page, setPage } = useUrlPage(FIRST_PAGE);

  // We need to grab all detailed shifts to calculate total number of shifts and total signups for an event
  const { data: detailedShifts, isPending: isPendingDetailedShifts } = useAllPagesRedVelvetQuery({
    enabled: isEventFetched && !!event.eventId,
    // choosing 25 for shifts page size since this reduces the likely number of pagination calls necessary and
    // because 99.3% of GIVEhub events has 10 or fewer shifts confirmed by Gil, as of 12/15/2025 - cce-6307
    redVelvetQueryKey: ['getEventDetailsByEventId', { eventId: event.eventId, pageSize: 25 }],
    converter: (output: GetEventDetailsByEventIdCommandOutput) => ({
      items: output.detailedShifts ?? [],
      next: output.page,
    }),
  });

  const isLoading = !isEventFetched || isPendingDetailedShifts;

  if (isLoading) {
    return (
      <Container>
        <Spinner />
        <span>{t('fullEvent.loadingEvent')}</span>
      </Container>
    );
  }

  const vcEditAction =
    page > 0 ? (
      <Button data-aci-analytics-name="trackAttendance_editEvent" onClick={() => setPage(0)}>
        {t('attendanceHeader.editEvent')}
      </Button>
    ) : undefined;

  const displayAttributes: DISPLAY_EVENT_ATTRIBUTES_TYPE[] = [
    DISPLAY_EVENT_ORGANIZATION_INFO,
    DISPLAY_EVENT_COORDINATOR_INFO,
    DISPLAY_EVENT_LOCATION_INFO,
    DISPLAY_EVENT_DATE_INFO,
    DISPLAY_SHIFTS_COUNT_INFO,
    DISPLAY_TOTAL_SIGNUPS_INFO,
  ];

  return (
    <Container>
      <EventTitleHeader
        event={event}
        editSource="trackAttendance"
        titleAnalyticsName="trackAttendance_title"
        vcEditAction={vcEditAction}
      />
      <EventCard
        event={event}
        detailedShifts={detailedShifts}
        displayAttributes={displayAttributes}
      />
    </Container>
  );
}
